import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, CheckCircle, Target, Rocket, Award, Users } from 'lucide-react';
import './Timeline.css';

const Timeline = () => {
  const milestones = [
    {
      year: "2025",
      quarter: "Q1",
      title: "Development Phase",
      description: "Building core platform features and MVP with limited resources",
      icon: <Target size={24} />,
      status: "current"
    },
    {
      year: "2025",
      quarter: "Q2",
      title: "Beta Testing",
      description: "Internal testing and refinement of core features",
      icon: <CheckCircle size={24} />,
      status: "upcoming"
    },
    {
      year: "2025",
      quarter: "Q3",
      title: "Limited Launch",
      description: "Small-scale launch to gather user feedback and validate concept",
      icon: <Users size={24} />,
      status: "upcoming"
    },
    {
      year: "2025",
      quarter: "Q4",
      title: "Seed Funding",
      description: "Seeking initial funding to accelerate development and team growth",
      icon: <Award size={24} />,
      status: "upcoming"
    },
    {
      year: "2026",
      quarter: "Q1",
      title: "Team Expansion",
      description: "Hiring key team members and scaling development efforts",
      icon: <Rocket size={24} />,
      status: "upcoming"
    },
    {
      year: "2026",
      quarter: "Q2",
      title: "Public Beta",
      description: "Wider public release with enhanced features and community",
      icon: <Rocket size={24} />,
      status: "upcoming"
    }
  ];

  return (
    <section className="timeline" id="timeline">
      <div className="container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          viewport={{ once: true }}
        >
          <h2 className="section-title">Our Journey</h2>
          <p className="section-subtitle">
            From concept to reality: The FitHive story
          </p>
        </motion.div>

        <div className="timeline-container">
          {milestones.map((milestone, index) => (
            <motion.div
              key={index}
              className={`timeline-item ${milestone.status}`}
              initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              viewport={{ once: true }}
            >
              <div className="timeline-marker">
                <div className="marker-icon">
                  {milestone.icon}
                </div>
                <div className="marker-line"></div>
              </div>
              
              <div className="timeline-content">
                <div className="timeline-header">
                  <div className="timeline-date">
                    <Calendar size={16} />
                    <span>{milestone.year} {milestone.quarter}</span>
                  </div>
                  <div className={`timeline-status ${milestone.status}`}>
                    {milestone.status === 'completed' && <CheckCircle size={16} />}
                    {milestone.status === 'current' && <Target size={16} />}
                    {milestone.status === 'upcoming' && <Rocket size={16} />}
                    <span>{milestone.status}</span>
                  </div>
                </div>
                
                <h3 className="timeline-title">{milestone.title}</h3>
                <p className="timeline-description">{milestone.description}</p>
                
                {milestone.status === 'current' && (
                  <motion.div
                    className="current-progress"
                    initial={{ width: 0 }}
                    whileInView={{ width: "100%" }}
                    transition={{ duration: 2, delay: 0.5 }}
                    viewport={{ once: true }}
                  >
                    <div className="progress-bar">
                      <div className="progress-fill"></div>
                    </div>
                    <span>In Progress</span>
                  </motion.div>
                )}
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="timeline-summary"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="summary-card">
            <h3>Development-First Approach</h3>
            <p>
              We're taking a lean, development-focused approach to build and validate our platform 
              before seeking major funding. This allows us to prove our concept and attract strategic partners.
            </p>
            <div className="next-milestones">
              <div className="milestone">
                <span className="milestone-label">Current Focus</span>
                <span className="milestone-value">Platform Development</span>
              </div>
              <div className="milestone">
                <span className="milestone-label">6-Month Goal</span>
                <span className="milestone-value">MVP Completion</span>
              </div>
              <div className="milestone">
                <span className="milestone-label">12-Month Goal</span>
                <span className="milestone-value">Seed Funding Round</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Timeline;
